/**
 * Coverage QA fixtures derived from real HAR captures (2026-04-07).
 *
 * Each fixture transforms a completed COVERAGE_REVIEW task into the shape
 * expected by the COVERAGE_REVIEW_QA next-pending-task endpoint:
 *
 *   QA task.data.original_result  ←  coverage task.result (scoped to one box)
 *   QA task.data.parcel_id        ←  coverage task.data.parcel_id
 *   QA task.data.original_task_id ←  coverage task.id
 *   QA task.image_id              ←  coverage task.image_id
 *
 * Planogram mock data uses the real IDs from the HAR so that the planogram
 * viewer cache key matches; product entries are synthetic (the atlas endpoint
 * was not captured in the HARs).
 */

import type { ProductSelection } from "@/types/coverage";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build a QA next-pending-task response from a single box of a completed
 * coverage review task.
 */
function buildQaTask(opts: {
  /** Unique ID for the QA task */
  id: string;
  /** Original coverage-review task ID */
  originalTaskId: string;
  /** Image ID (top-level on the coverage task) */
  imageId: string;
  /** Parcel ID from coverage task data */
  parcelId: string;
  /** The single empty-area box being reviewed */
  emptyArea: {
    id: string;
    points: number[][];
    confidence: number;
    class_index: number;
  };
  /** The matching product selection for this box */
  productSelection: ProductSelection;
}) {
  return {
    id: opts.id,
    type: "COVERAGE_REVIEW_QA",
    status: "PENDING",
    image_id: opts.imageId,
    assigned_to: "daniel",
    completed_by: null,
    created_at: "2026-04-07T23:12:00.000000+00:00",
    updated_at: "2026-04-07T23:12:00.000000+00:00",
    started_at: "2026-04-08T03:12:00.000000+00:00",
    completed_at: null,
    actively_labeling: true,
    last_renewed_at: "2026-04-08T03:12:00.000000+00:00",
    validated: null,
    description: "",
    metadata: {},
    data: {
      parcel_id: opts.parcelId,
      original_task_id: opts.originalTaskId,
      original_result: {
        // Full format — CoverageQaTask reads empty_areas[0] and product_selections[0]
        empty_areas: [opts.emptyArea],
        product_selections: [opts.productSelection],
        // Simplified format — CoverageQaTask also checks these as fallbacks
        box: { points: opts.emptyArea.points },
        product_selection: opts.productSelection,
      },
    },
  };
}

// ---------------------------------------------------------------------------
// HAR 1 — JOB_68409615 / task 019d5af6-908c (Kroger Monroe, 2 boxes)
// ---------------------------------------------------------------------------

/** Box 0: upper-middle shelf gap, single product */
export const harTask1Box0 = buildQaTask({
  id: "qa-har1-box0",
  originalTaskId: "019d5af6-908c-1a60-dd0e-dd2570188ad9",
  imageId: "019d5899-8139-6637-c63f-b8a911f6831a",
  parcelId: "019d5af4-d0fa-9ceb-5174-b29b6287123a",
  emptyArea: {
    id: "d0c0f963-2c25-4d2f-b853-1701849e6bf1",
    points: [
      [0.29333758409165955, 0.2113225745204504],
      [0.4916596200018138, 0.2113225745204504],
      [0.4916596200018138, 0.2711973417179016],
      [0.29333758409165955, 0.2711973417179016],
    ],
    confidence: 1,
    class_index: 0,
  },
  productSelection: {
    bay_index: 0,
    product_ids: ["019c02ad-aa47-87d5-bf40-b282bbed66bc"],
    planogram_id: "019c5989-b32b-21c7-488d-ae3bb1147f7c",
    empty_area_index: 0,
  },
});

/** Box 1: lower-middle shelf gap, single product */
export const harTask1Box1 = buildQaTask({
  id: "qa-har1-box1",
  originalTaskId: "019d5af6-908c-1a60-dd0e-dd2570188ad9",
  imageId: "019d5899-8139-6637-c63f-b8a911f6831a",
  parcelId: "019d5af4-d0fa-9ceb-5174-b29b6287123a",
  emptyArea: {
    id: "88370cf7-24ab-409f-accc-dcdbb9f716e4",
    points: [
      [0.38409512594884876, 0.3317023696226944],
      [0.5745739175009743, 0.3317023696226944],
      [0.5745739175009743, 0.4060731330890023],
      [0.38409512594884876, 0.4060731330890023],
    ],
    confidence: 1,
    class_index: 0,
  },
  productSelection: {
    bay_index: 0,
    product_ids: ["019c02ad-a959-8141-8bf5-28c4e9caddba"],
    planogram_id: "019c5989-b32b-21c7-488d-ae3bb1147f7c",
    empty_area_index: 1,
  },
});

// ---------------------------------------------------------------------------
// HAR 2 — JOB_68409351 / task 019d5af5-b124 (Kroger Monroe, 4 boxes, 2 with products)
// ---------------------------------------------------------------------------

/** Box 1 (of 4): right-side shelf, single product on different planogram */
export const harTask2Box1 = buildQaTask({
  id: "qa-har2-box1",
  originalTaskId: "019d5af5-b124-323a-e760-f08759bff39d",
  imageId: "019d589b-626e-602f-9ebb-cbebe16d3420",
  parcelId: "019d5af4-c162-8933-73d1-b9fda6e0129e",
  emptyArea: {
    id: "96622c29-d319-4fc5-9455-ff630fa1ebbf",
    // From HAR: second empty area
    points: [
      [0.5986849480446684, 0.02134264648816453],
      [0.7987453110852413, 0.02134264648816453],
      [0.7987453110852413, 0.1564513477485362],
      [0.5986849480446684, 0.1564513477485362],
    ],
    confidence: 1,
    class_index: 0,
  },
  productSelection: {
    bay_index: 0,
    product_ids: ["019c02a4-fabe-cf27-b922-f209f709c858"],
    planogram_id: "019c5989-b32a-8712-99f2-182af3a7d312",
    empty_area_index: 1,
  },
});

/** Box 3 (of 4): lower shelf, single product */
export const harTask2Box3 = buildQaTask({
  id: "qa-har2-box3",
  originalTaskId: "019d5af5-b124-323a-e760-f08759bff39d",
  imageId: "019d589b-626e-602f-9ebb-cbebe16d3420",
  parcelId: "019d5af4-c162-8933-73d1-b9fda6e0129e",
  emptyArea: {
    id: "lower-shelf-box3",
    points: [
      [0.3, 0.45],
      [0.5, 0.45],
      [0.5, 0.55],
      [0.3, 0.55],
    ],
    confidence: 1,
    class_index: 0,
  },
  productSelection: {
    bay_index: 0,
    product_ids: ["019c4b97-b25a-fcb2-ee73-888bb9de25a9"],
    planogram_id: "019c5989-b32a-8712-99f2-182af3a7d312",
    empty_area_index: 3,
  },
});

// ---------------------------------------------------------------------------
// HAR 3 — JOB_68408909 / task 019d5af5-1186 (Kroger Monroe, 2 boxes)
// ---------------------------------------------------------------------------

/** Box 0: left-side bottom shelf, small box, auto-detected */
export const harTask3Box0 = buildQaTask({
  id: "qa-har3-box0",
  originalTaskId: "019d5af5-1186-b911-23cf-92d51c862be5",
  imageId: "019d589b-d3f7-074e-d7b1-6f5650872eef",
  parcelId: "019d5af4-b775-57cb-f90a-4d6b7b214689",
  emptyArea: {
    id: "c02ab3d9-fe5f-4107-922b-febf3fe18955",
    points: [
      [0.03240966796875, 0.47210693359375],
      [0.09368896484375, 0.47210693359375],
      [0.09368896484375, 0.52642822265625],
      [0.03240966796875, 0.52642822265625],
    ],
    confidence: 0.607421875,
    class_index: 1,
  },
  productSelection: {
    bay_index: 0,
    product_ids: ["019c0291-552d-8db2-ee13-2e514de51af1"],
    planogram_id: "019c5989-b32a-7040-aeae-8923c43b1c08",
    empty_area_index: 0,
  },
});

/** Box 1: center shelf, human-drawn */
export const harTask3Box1 = buildQaTask({
  id: "qa-har3-box1",
  originalTaskId: "019d5af5-1186-b911-23cf-92d51c862be5",
  imageId: "019d589b-d3f7-074e-d7b1-6f5650872eef",
  parcelId: "019d5af4-b775-57cb-f90a-4d6b7b214689",
  emptyArea: {
    id: "2f5a5833-958a-4b3a-ba03-9b2a63aa7aaf",
    points: [
      [0.5700356644642327, 0.29067705305671104],
      [0.623588702569279, 0.29067705305671104],
      [0.623588702569279, 0.31642370599182945],
      [0.5700356644642327, 0.31642370599182945],
    ],
    confidence: 1,
    class_index: 0,
  },
  productSelection: {
    bay_index: 0,
    product_ids: ["019c0291-9b97-205c-be04-f970e9d50fd7"],
    planogram_id: "019c5989-b32a-7040-aeae-8923c43b1c08",
    empty_area_index: 1,
  },
});

// ---------------------------------------------------------------------------
// Shared store data (all parcels are from Kroger Monroe)
// ---------------------------------------------------------------------------

const krogerMonroeStore = {
  id: "S_116",
  name: "Kroger - Monroe",
  atlas_id: "019bc852-9c61-2025-d48d-7bff510e8816",
  site_id: "kroger-336",
};

// ---------------------------------------------------------------------------
// Parcel data (from HAR responses — enriched with all fields needed by
// getNextPendingTaskData and CoverageTask page)
// ---------------------------------------------------------------------------

export const harParcel1 = {
  parcel_id: "019d5af4-d0fa-9ceb-5174-b29b6287123a",
  batch_id: "019d5af0-7001-040f-1715-c048a24ea4f4",
  video_id: "019d5899-60ac-3533-7900-de7f55d9eb27",
  store: krogerMonroeStore,
  references: [] as unknown[],
  query_empty_area_auto_routing: [] as boolean[],
  query_to_product_index: null,
  metadata: {},
  query: {
    context_id: "019d5af4-d0a4-ec93-8b93-f6bb6b310f26",
    image_id: "019d5899-8139-6637-c63f-b8a911f6831a",
    batch_id: "019d5af0-7001-040f-1715-c048a24ea4f4",
    video_id: "019d5899-60ac-3533-7900-de7f55d9eb27",
    embedding: null,
    aisle_detections: [
      {
        id: "eedcde10-3919-4481-9f0a-d9896846e2d3",
        points: [[0.0, 0.0], [1.0, 0.0], [1.0, 1.0], [0.0, 1.0]],
        class_index: null,
        confidence: null,
      },
    ],
    aisle_to_planogram: ["019c5989-b32b-21c7-488d-ae3bb1147f7c"],
    empty_area_detections: [
      {
        id: "8d49a252-877f-4041-9be7-dc584f6aa6f1",
        points: [[0.2998046875, 0.12542724609375], [0.58154296875, 0.12542724609375], [0.2998046875, 0.27984619140625], [0.58154296875, 0.27984619140625]],
        class_index: 1,
        confidence: 0.8408203125,
      },
      {
        id: "7f12c88e-7824-4841-9a93-dd8e273f572b",
        points: [[0.49957275390625, 0.27685546875], [0.58831787109375, 0.27685546875], [0.49957275390625, 0.404296875], [0.58831787109375, 0.404296875]],
        class_index: 1,
        confidence: 0.6826171875,
      },
    ],
    empty_area_embeddings: null,
    empty_area_to_product: null,
    empty_area_to_aisle: [0, 0],
    // Product detections plotted on the shelf image. Positionally aligned with
    // product_ids below: [0]→Cereal Oats, [1]→Granola Crunch (both in
    // harPlanogramA), [2]→null (unmatched — renders dimmed, hover/click no-op).
    // [0] and [1] deliberately OVERLAP (x∈[0.24,0.32]) to simulate the
    // angled-camera case: nearest-center hit-testing should split that band so
    // both remain hoverable/clickable.
    product_detections: [
      {
        id: "det-har1-0",
        points: [
          [0.1, 0.55],
          [0.32, 0.55],
          [0.32, 0.82],
          [0.1, 0.82],
        ],
        class_index: 0,
        confidence: 0.91,
      },
      {
        id: "det-har1-1",
        points: [
          [0.24, 0.58],
          [0.46, 0.58],
          [0.46, 0.85],
          [0.24, 0.85],
        ],
        class_index: 0,
        confidence: 0.88,
      },
      {
        id: "det-har1-2",
        points: [
          [0.7, 0.6],
          [0.85, 0.6],
          [0.85, 0.8],
          [0.7, 0.8],
        ],
        class_index: 0,
        confidence: 0.55,
      },
    ],
    product_embeddings: null,
    product_ids: [
      "019c02ad-aa47-87d5-bf40-b282bbed66bc", // Cereal Oats 16oz (in harPlanogramA)
      "019c02ad-a959-8141-8bf5-28c4e9caddba", // Granola Crunch 12oz (in harPlanogramA)
      null, // unmatched detection
    ],
  },
};

export const harParcel2 = {
  parcel_id: "019d5af4-c162-8933-73d1-b9fda6e0129e",
  batch_id: "019d5af0-7001-040f-1715-c048a24ea4f4",
  video_id: "019d589b-5951-2ff9-1b47-ca4b915152f7",
  store: krogerMonroeStore,
  references: [] as unknown[],
  query_empty_area_auto_routing: [] as boolean[],
  query_to_product_index: null,
  metadata: {},
  query: {
    context_id: "019d5af4-c162-0000-0000-000000000000",
    image_id: "019d589b-626e-602f-9ebb-cbebe16d3420",
    batch_id: "019d5af0-7001-040f-1715-c048a24ea4f4",
    video_id: "019d589b-5951-2ff9-1b47-ca4b915152f7",
    embedding: null,
    aisle_detections: [
      {
        id: "aisle-det-har2",
        points: [[0.0, 0.0], [1.0, 0.0], [1.0, 1.0], [0.0, 1.0]],
        class_index: null,
        confidence: null,
      },
    ],
    aisle_to_planogram: ["019c5989-b32a-8712-99f2-182af3a7d312"],
    empty_area_detections: [],
    empty_area_embeddings: null,
    empty_area_to_product: null,
    empty_area_to_aisle: [0, 0, 0, 0],
    product_detections: [],
    product_embeddings: null,
    product_ids: null,
  },
};

export const harParcel3 = {
  parcel_id: "019d5af4-b775-57cb-f90a-4d6b7b214689",
  batch_id: "019d5af0-7001-040f-1715-c048a24ea4f4",
  video_id: "019d589b-a664-2de9-0e34-a231fc675270",
  store: krogerMonroeStore,
  references: [] as unknown[],
  query_empty_area_auto_routing: [] as boolean[],
  query_to_product_index: null,
  metadata: {},
  query: {
    context_id: "019d5af4-b775-0000-0000-000000000000",
    image_id: "019d589b-d3f7-074e-d7b1-6f5650872eef",
    batch_id: "019d5af0-7001-040f-1715-c048a24ea4f4",
    video_id: "019d589b-a664-2de9-0e34-a231fc675270",
    embedding: null,
    aisle_detections: [
      {
        id: "aisle-det-har3",
        points: [[0.0, 0.0], [1.0, 0.0], [1.0, 1.0], [0.0, 1.0]],
        class_index: null,
        confidence: null,
      },
    ],
    aisle_to_planogram: ["019c5989-b32a-7040-aeae-8923c43b1c08"],
    empty_area_detections: [
      {
        id: "c02ab3d9-fe5f-4107-922b-febf3fe18955",
        points: [[0.03240966796875, 0.47210693359375], [0.09368896484375, 0.47210693359375], [0.09368896484375, 0.52642822265625], [0.03240966796875, 0.52642822265625]],
        class_index: 1,
        confidence: 0.607421875,
      },
      {
        id: "a67d1a1a-31b7-4b0c-8caf-924ddcf7bfb1",
        points: [[0.173553466796875, 0.4748992919921875], [0.235137939453125, 0.4748992919921875], [0.235137939453125, 0.5361785888671875], [0.173553466796875, 0.5361785888671875]],
        class_index: 1,
        confidence: 0.42333984375,
      },
    ],
    empty_area_embeddings: null,
    empty_area_to_product: null,
    empty_area_to_aisle: [0, 0],
    product_detections: [],
    product_embeddings: null,
    product_ids: null,
  },
};

// ---------------------------------------------------------------------------
// Job data (from HAR responses)
// ---------------------------------------------------------------------------

export const harJob1 = {
  id: "JOB_68409615",
  store: {
    id: "S_116",
    site_id: "kroger-336",
    name: "Kroger - Monroe",
    atlas_id: "019bc852-9c61-2025-d48d-7bff510e8816",
  },
  type: "COVERAGE_REVIEW_QA_TASKS",
  status: "IN_PROGRESS",
  testing: false,
  human_review: true,
  description: "",
  created_at: "2026-04-05T00:06:30.722273+00:00",
  updated_at: "2026-04-07T19:38:25.947241+00:00",
};

export const harJob2 = {
  ...harJob1,
  id: "JOB_68409351",
};

export const harJob3 = {
  ...harJob1,
  id: "JOB_68408909",
};

// ---------------------------------------------------------------------------
// Synthetic planogram data keyed by the real planogram IDs from the HARs.
// (Atlas responses were not captured — products are placeholders matching
// the real product IDs from task submissions.)
//
// Shape: PlanogramDetailSchema from packages/api/atlas/types.gen.ts
// Products use {x, y} points (not number[][]) as the PlanogramViewer expects.
// ---------------------------------------------------------------------------

/** Helper to build a mock product matching PlanogramProductSchema. */
function mockProduct(opts: {
  productId: string;
  description: string;
  /** Top-left x,y and width,height in planogram pixel coords */
  x: number;
  y: number;
  w: number;
  h: number;
  retailerId: string;
}) {
  return {
    id: opts.productId, // used as unique key in viewer
    product_id: opts.productId,
    description: opts.description,
    identifier: null,
    retailer_id: opts.retailerId,
    facings: 1,
    points: [
      { x: opts.x, y: opts.y },
      { x: opts.x + opts.w, y: opts.y },
      { x: opts.x + opts.w, y: opts.y + opts.h },
      { x: opts.x, y: opts.y + opts.h },
    ],
    gs_image_url: null,
    image_url: `https://via.placeholder.com/${opts.w}x${opts.h}?text=${encodeURIComponent(opts.description.slice(0, 12))}`,
    override_description: null,
    override_image_url: null,
    metadata: {},
    price: null,
  };
}

const KROGER_RETAILER = {
  id: "019bc852-9c61-2025-d48d-7bff510e8816",
  name: "Kroger",
  short_name: "KR",
};
const KROGER_RETAILER_ID = KROGER_RETAILER.id;

/** Planogram A — used by HAR task 1 (2 products + 1 alternative) */
export const harPlanogramA = {
  id: "019c5989-b32b-21c7-488d-ae3bb1147f7c",
  description: "Kroger Monroe — Aisle Planogram A",
  type: "STANDARD",
  status: "ACTIVE",
  finalized: true,
  planogram_group_id: "plano-group-a",
  store_ids: ["S_116"],
  metadata: {},
  retailer: KROGER_RETAILER,
  created_at: "2026-01-01T00:00:00+00:00",
  updated_at: "2026-04-01T00:00:00+00:00",
  image_url: "https://via.placeholder.com/600x400?text=Planogram+A",
  products: [
    mockProduct({ productId: "019c02ad-aa47-87d5-bf40-b282bbed66bc", description: "Cereal Oats 16oz", x: 10, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c02ad-a959-8141-8bf5-28c4e9caddba", description: "Granola Crunch 12oz", x: 140, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c02ad-0000-0000-0000-alternative01", description: "Rice Puffs 14oz", x: 270, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c02ad-0000-0000-0000-alternative02", description: "Wheat Flakes 18oz", x: 400, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c02ad-0000-0000-0000-alternative03", description: "Corn Squares 10oz", x: 10, y: 180, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c02ad-0000-0000-0000-alternative04", description: "Bran Clusters 15oz", x: 140, y: 180, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
  ],
};

/** Planogram B — used by HAR task 2 (2 products + 1 alternative) */
export const harPlanogramB = {
  id: "019c5989-b32a-8712-99f2-182af3a7d312",
  description: "Kroger Monroe — Aisle Planogram B",
  type: "STANDARD",
  status: "ACTIVE",
  finalized: true,
  planogram_group_id: "plano-group-b",
  store_ids: ["S_116"],
  metadata: {},
  retailer: KROGER_RETAILER,
  created_at: "2026-01-01T00:00:00+00:00",
  updated_at: "2026-04-01T00:00:00+00:00",
  image_url: "https://via.placeholder.com/600x400?text=Planogram+B",
  products: [
    mockProduct({ productId: "019c02a4-fabe-cf27-b922-f209f709c858", description: "Pasta Sauce 24oz", x: 10, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c4b97-b25a-fcb2-ee73-888bb9de25a9", description: "Spaghetti 16oz", x: 140, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c02a4-0000-0000-0000-alternative01", description: "Penne Rigate 16oz", x: 270, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c02a4-0000-0000-0000-alternative02", description: "Marinara 26oz", x: 400, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c02a4-0000-0000-0000-alternative03", description: "Alfredo Sauce 15oz", x: 10, y: 180, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c02a4-0000-0000-0000-alternative04", description: "Rotini 12oz", x: 140, y: 180, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
  ],
};

/** Planogram C — used by HAR task 3 (2 products + 1 alternative) */
export const harPlanogramC = {
  id: "019c5989-b32a-7040-aeae-8923c43b1c08",
  description: "Kroger Monroe — Aisle Planogram C",
  type: "STANDARD",
  status: "ACTIVE",
  finalized: true,
  planogram_group_id: "plano-group-c",
  store_ids: ["S_116"],
  metadata: {},
  retailer: KROGER_RETAILER,
  created_at: "2026-01-01T00:00:00+00:00",
  updated_at: "2026-04-01T00:00:00+00:00",
  image_url: "https://via.placeholder.com/600x400?text=Planogram+C",
  products: [
    mockProduct({ productId: "019c0291-552d-8db2-ee13-2e514de51af1", description: "Chips Original 10oz", x: 10, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c0291-9b97-205c-be04-f970e9d50fd7", description: "Pretzels Twist 8oz", x: 140, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c0291-0000-0000-0000-alternative01", description: "Tortilla Chips 13oz", x: 270, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c0291-0000-0000-0000-alternative02", description: "Popcorn 5.5oz", x: 400, y: 10, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c0291-0000-0000-0000-alternative03", description: "Cheese Puffs 8oz", x: 10, y: 180, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
    mockProduct({ productId: "019c0291-0000-0000-0000-alternative04", description: "Trail Mix 10oz", x: 140, y: 180, w: 120, h: 160, retailerId: KROGER_RETAILER_ID }),
  ],
};

// ---------------------------------------------------------------------------
// Image gallery mocks (signed URLs expire — use placeholders for dev)
// ---------------------------------------------------------------------------

export const harImageGallery1 = {
  images: [
    {
      id: "019d5899-8139-6637-c63f-b8a911f6831a",
      image: "https://via.placeholder.com/1920x1080?text=Shelf+Image+HAR1",
    },
  ],
};

export const harImageGallery2 = {
  images: [
    {
      id: "019d589b-626e-602f-9ebb-cbebe16d3420",
      image: "https://via.placeholder.com/1920x1080?text=Shelf+Image+HAR2",
    },
  ],
};

export const harImageGallery3 = {
  images: [
    {
      id: "019d589b-d3f7-074e-d7b1-6f5650872eef",
      image: "https://via.placeholder.com/1920x1080?text=Shelf+Image+HAR3",
    },
  ],
};

// ---------------------------------------------------------------------------
// Lookup maps — used by fixture-sets.ts to wire the right parcel/planogram
// for each QA task.
// ---------------------------------------------------------------------------

/** All QA tasks indexed by ID for easy lookup */
export const harQaTasks = {
  "qa-har1-box0": harTask1Box0,
  "qa-har1-box1": harTask1Box1,
  "qa-har2-box1": harTask2Box1,
  "qa-har2-box3": harTask2Box3,
  "qa-har3-box0": harTask3Box0,
  "qa-har3-box1": harTask3Box1,
} as const;

/** Maps parcel ID → parcel fixture */
export const harParcels: Record<string, unknown> = {
  "019d5af4-d0fa-9ceb-5174-b29b6287123a": harParcel1,
  "019d5af4-c162-8933-73d1-b9fda6e0129e": harParcel2,
  "019d5af4-b775-57cb-f90a-4d6b7b214689": harParcel3,
};

/** Maps planogram ID → planogram fixture */
export const harPlanograms: Record<string, unknown> = {
  "019c5989-b32b-21c7-488d-ae3bb1147f7c": harPlanogramA,
  "019c5989-b32a-8712-99f2-182af3a7d312": harPlanogramB,
  "019c5989-b32a-7040-aeae-8923c43b1c08": harPlanogramC,
};

/** Maps image ID → image gallery fixture */
export const harImageGalleries: Record<string, unknown> = {
  "019d5899-8139-6637-c63f-b8a911f6831a": harImageGallery1,
  "019d589b-626e-602f-9ebb-cbebe16d3420": harImageGallery2,
  "019d589b-d3f7-074e-d7b1-6f5650872eef": harImageGallery3,
};
